/**
 * API client de Estoque (Fase 7).
 *
 *   GET    /api/produtos
 *   POST   /api/produtos
 *   GET    /api/produtos/{id}
 *   PATCH  /api/produtos/{id}
 *   DELETE /api/produtos/{id}
 *
 *   GET    /api/estoque-movimentos?produto={id}&tipo=&origem=
 *   POST   /api/estoque-movimentos
 */

const PREF_P = "/api/produtos";
const PREF_M = "/api/estoque-movimentos";

// ---------- Produto ----------

export function listarProdutos(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.busca) usp.set("busca", params.busca);
  if (params.categoria) usp.set("categoria", params.categoria);
  if (params.ativo !== undefined) usp.set("ativo", params.ativo ? "true" : "false");
  // A pergunta que o almoxarife faz de manhã: "o que está faltando?".
  if (params.abaixoMinimo) usp.set("abaixo_minimo", "true");
  if (params.pendente !== undefined) usp.set("pendente", String(params.pendente));
  // Plano de Suprimentos: filtros do cadastro unificado (D04).
  if (params.tipoSuprimento) usp.set("tipo_suprimento", params.tipoSuprimento);
  if (params.fabricante) usp.set("fabricante", params.fabricante);
  if (params.cor) usp.set("cor", params.cor);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREF_P}${qs ? `?${qs}` : ""}`);
}

export function obterProduto(request, id) {
  return request(`${PREF_P}/${id}`);
}

export function criarProduto(request, dados) {
  return request(PREF_P, { method: "POST", body: JSON.stringify(dados) });
}

/** Envia (ou troca) a textura do material: multipart {arquivo}. */
export function enviarTextura(request, id, arquivo) {
  const formData = new FormData();
  formData.append("arquivo", arquivo);
  return request(`${PREF_P}/${id}/textura`, { method: "POST", body: formData, headers: {}, silentError: true });
}

export function removerTextura(request, id) {
  return request(`${PREF_P}/${id}/textura`, { method: "DELETE", silentError: true });
}

/**
 * Depois de salvar o material: sobe a textura escolhida no formulário ou
 * remove a que existia. Erro aqui NÃO desfaz o material — quem chama avisa.
 */
export async function aplicarTextura(request, produtoId, editando) {
  if (!produtoId) return null;
  if (editando?.textura_arquivo) return enviarTextura(request, produtoId, editando.textura_arquivo);
  if (editando?.textura_remover) return removerTextura(request, produtoId);
  return null;
}

export function atualizarProduto(request, id, dados) {
  return request(`${PREF_P}/${id}`, { method: "PATCH", body: JSON.stringify(dados) });
}

export function deletarProduto(request, id) {
  return request(`${PREF_P}/${id}`, { method: "DELETE" });
}

const LOTE_EXCLUSAO = 200;

async function _emFatias(request, caminho, ids, somar) {
  for (let i = 0; i < ids.length; i += LOTE_EXCLUSAO) {
    const r = await request(`${PREF_P}/${caminho}`, {
      method: "POST",
      body: JSON.stringify({ ids: ids.slice(i, i + LOTE_EXCLUSAO) }),
    });
    somar(r);
  }
}

/**
 * Apaga varios produtos do Cadastro em fatias de 200 e soma o resultado:
 * { apagados, recusados: [{id, codigo, nome, motivo}], resumo: {motivo: n}, nao_encontrados }.
 * Recusado = em uso (movimentos, sobras, reservas...); a tela oferece desativar.
 */
export async function excluirProdutos(request, ids) {
  const total = { apagados: 0, recusados: [], resumo: {}, nao_encontrados: 0 };
  await _emFatias(request, "excluir", ids, (r) => {
    total.apagados += r.apagados || 0;
    total.recusados.push(...(r.recusados || []));
    total.nao_encontrados += r.nao_encontrados || 0;
    Object.entries(r.resumo || {}).forEach(([k, n]) => { total.resumo[k] = (total.resumo[k] || 0) + n; });
  });
  return total;
}

/** Desativa varios produtos (segundo passo da lixeira). Devolve { desativados }. */
export async function desativarProdutos(request, ids) {
  const total = { desativados: 0 };
  await _emFatias(request, "desativar", ids, (r) => { total.desativados += r.desativados || 0; });
  return total;
}

// ---------- Movimento ----------

export function listarMovimentos(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.produto) usp.set("produto", params.produto);
  if (params.tipo) usp.set("tipo", params.tipo);
  if (params.origem) usp.set("origem", params.origem);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREF_M}${qs ? `?${qs}` : ""}`);
}

export function criarMovimento(request, dados) {
  return request(PREF_M, { method: "POST", body: JSON.stringify(dados) });
}


/**
 * Catálogo de tipos de suprimento (ADR-0061 D1).
 *
 * Vem do backend e não é duplicado aqui de propósito: manter a lista nos
 * dois lados foi como o catálogo de MDF virou uma segunda verdade, com um
 * preço que ninguém lia.
 */
export function listarTiposSuprimento(request) {
  return request("/api/produtos/tipos");
}

// ---------- Depósito (Bloco 6 do ADR-0061) ----------

const PREF_D = "/api/depositos";

export function listarDepositos(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.ativo !== undefined) {
    usp.set("ativo", params.ativo ? "true" : "false");
  }
  const qs = usp.toString();
  return request(`${PREF_D}${qs ? `?${qs}` : ""}`);
}

export function criarDeposito(request, dados) {
  return request(PREF_D, { method: "POST", body: JSON.stringify(dados) });
}

export function atualizarDeposito(request, id, dados) {
  return request(`${PREF_D}/${id}`, {
    method: "PATCH", body: JSON.stringify(dados),
  });
}

/** Desativa (soft). Bloco 6: apagar de verdade e `apagarDeposito`. */
export function desativarDeposito(request, id) {
  return request(`${PREF_D}/${id}/desativar`, { method: "POST" });
}

/**
 * Apaga de vez. Se algo aponta para o deposito, o backend devolve 409 com
 * `em_uso: true` e a tela oferece desativar no lugar.
 */
export function apagarDeposito(request, id) {
  return request(`${PREF_D}/${id}`, { method: "DELETE", silentError: true });
}

/** Onde está o saldo deste produto, lugar a lugar. */
export function saldoPorDeposito(request, produtoId) {
  return request(`${PREF_P}/${produtoId}/saldo-por-deposito`);
}

/**
 * Move material entre depósitos — vira um par de movimentos no backend.
 * O saldo da empresa não muda; o de cada lugar sim.
 */
export function transferirEntreDepositos(request, dados) {
  return request(`${PREF_M}/transferir`, {
    method: "POST", body: JSON.stringify(dados),
  });
}

// ---------- ADR-0070: Cadastro de Suprimentos redesenhado ----------

/** Onde o item é usado (projetos, requisições, OCs, sobras, reservas…). */
export function usosDoProduto(request, produtoId) {
  return request(`${PREF_P}/${produtoId}/usos`);
}

/** Movimentos do item com `saldo_apos` (acumulado no backend). `dias` 0 = tudo. */
export function movimentosDoProduto(request, produtoId, { dias = 90, tipo = "" } = {}) {
  const usp = new URLSearchParams({ dias: String(dias) });
  if (tipo) usp.set("tipo", tipo);
  return request(`${PREF_P}/${produtoId}/movimentos?${usp.toString()}`);
}

/**
 * Escreve um campo em vários produtos (barra de ações em massa):
 * `campo` = "categoria" | "estoque_minimo". Devolve {atualizados, inalterados, nao_encontrados}.
 * Inativar em massa é `desativarProdutos`.
 */
export function atualizarProdutosLote(request, ids, campo, valor) {
  return request(`${PREF_P}/lote`, { method: "POST", body: JSON.stringify({ ids, campo, valor }) });
}

